import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link } from 'raviger';
import { Card, Col, Row } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import Header from '../components/Header';
import Scrollbar from '../components/Scrollbars';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';

const customStyles = {
  control: (styles, { isDisabled }) => {
    return {
      ...styles,
      color: '#FFF',
      cursor: isDisabled ? 'not-allowed' : 'default',
      backgroundColor: isDisabled ? '#DFDDDD' : 'white',
    }
  },
  option: provided => ({
    ...provided,
    color: 'black'
  }),
  singleValue: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#595959' : 'black'
  }),
  menu: provided => ({ ...provided, zIndex: 9999 })
};

const SPECIES_CATEGORIES = ['cat', 'dog', 'avian', 'reptile/amphibian', 'fish', 'small_mammals', 'equine', 'ruminant', 'camelid', 'swine', 'other'];

function ClaimsDashboard({ incident, organization }) {

  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({ claims: [], isFetching: true });
  const [reportData, setReportData] = useState([]);
  const [shelterOptions, setShelterOptions] = useState([]);
  const [selectedShelter, setSelectedShelter] = useState('all');
  const [animalData, setAnimalData] = useState({});

  useEffect(() => {
    let unmounted = false;
    const source = axios.CancelToken.source();

    const fetchData = async () => {
      await axios.get('/shelter/api/shelter/?incident=' + incident + '&organization=' + organization, {
        cancelToken: source.token,
      })
      .then(async (shelterResponse) => {
        if (unmounted) {
          return;
        }
        const claimsResponse = await axios.get('/reunification/api/claim/?incident=' + incident + '&organization=' + organization, {
          cancelToken: source.token,
        });
        if (unmounted) {
          return;
        }

        // Only open claims are tracked per-animal, since that's all this page lists.
        let animal_data = {};
        let counts_by_status = { open: {}, approved: {}, denied: {} };
        SPECIES_CATEGORIES.forEach(category => {
          counts_by_status.open[category] = 0;
          counts_by_status.approved[category] = 0;
          counts_by_status.denied[category] = 0;
        });

        claimsResponse.data.forEach(claim => {
          const animal = claim.animal_object;
          if (!animal) {
            return;
          }
          if (counts_by_status[claim.status] && counts_by_status[claim.status][animal.category] !== undefined) {
            counts_by_status[claim.status][animal.category] += 1;
          }
          if (claim.status === 'open') {
            if (!Object.keys(animal_data).includes(String(animal.id))) {
              animal_data[animal.id] = { ...animal, open: 0 };
            }
            animal_data[animal.id].open += 1;
          }
        });

        let report_data = SPECIES_CATEGORIES.filter(category => counts_by_status.open[category] + counts_by_status.approved[category] + counts_by_status.denied[category] > 0)
          .map(category => ({
            species: category,
            open: counts_by_status.open[category],
            approved: counts_by_status.approved[category],
            denied: counts_by_status.denied[category],
          }));
        report_data.push({
          species: 'total',
          open: report_data.reduce((a, v) => a + v.open, 0),
          approved: report_data.reduce((a, v) => a + v.approved, 0),
          denied: report_data.reduce((a, v) => a + v.denied, 0),
        });

        setReportData(report_data);
        setData({ claims: claimsResponse.data, isFetching: false });
        setAnimalData(animal_data);

        // Open-claim counts per location, shown alongside each Location option.
        let open_by_shelter = { all: 0, field: 0 };
        Object.values(animal_data).forEach(animal => {
          open_by_shelter.all += animal.open;
          const key = animal.shelter === null ? 'field' : animal.shelter;
          open_by_shelter[key] = (open_by_shelter[key] || 0) + animal.open;
        });

        let shelter_options = [{ value: 'all', label: `All Animals (${open_by_shelter.all} claims)` }];
        shelterResponse.data.forEach(shelter => {
          shelter_options.push({ value: shelter.id, label: `${shelter.name} (${open_by_shelter[shelter.id] || 0} claims)` });
        });
        shelter_options.push({ value: 'field', label: `Field Animals (${open_by_shelter.field} claims)` });
        setShelterOptions(shelter_options);
      })
      .catch(() => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };

    fetchData();

    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [incident, organization, setShowSystemError]);

  const claim_columns = [
    {
      name: 'Species',
      selector: row => row.species ? row.species[0].toUpperCase() + row.species.slice(1) : row.species,
    },
    {
      name: 'Open',
      selector: row => row.open,
    },
    {
      name: 'Approved',
      selector: row => row.approved,
    },
    {
      name: 'Denied',
      selector: row => row.denied,
    },
  ];

  const visibleAnimalIds = Object.keys(animalData)
    .filter(animal_id => selectedShelter === 'field' ? animalData[animal_id].shelter === null : selectedShelter !== 'all' ? animalData[animal_id].shelter === selectedShelter : true);

  const selectedOption = shelterOptions.find(option => option.value === selectedShelter) || null;

  return (
    <>
      <Header>Reunification</Header>
      <DataTable
        columns={claim_columns}
        data={reportData}
        title={'Claims by Species'}
        striped
        noDataComponent={!data.isFetching ? <div style={{ padding: "24px" }}>There are currently no claims</div> : <div style={{ padding: "24px" }}>Fetching claims data...</div>}
      />
      <p className="phr" />
      <div className="row mb-2">
        <div className="col-12 d-flex">
          <Card className="mb-2 border rounded" style={{ width: "100%" }}>
            <Card.Body style={{ marginBottom: "" }}>
              <Card.Title style={{ marginTop: "-2px", marginBottom: "-10px" }}>
                <h4>
                  <Row className="d-flex gx-2">
                    <Col xs={6}>
                      <label className="mb-2">Location</label>
                      <Select
                        id="status"
                        type="text"
                        name="status"
                        options={shelterOptions}
                        styles={customStyles}
                        value={selectedOption}
                        onChange={(instance) => {
                          setSelectedShelter(instance === null ? '' : instance.value);
                        }}
                      />
                    </Col>
                  </Row>
                </h4>
              </Card.Title>
            </Card.Body>
          </Card>
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-12 d-flex">
          <Card className="mb-2 border rounded" style={{ width: "100%", height: "385px" }}>
            <Card.Body style={{ marginBottom: "" }}>
              <Card.Title style={{ marginTop: "-2px", marginBottom: "17px" }}>
                <h4 className="mb-0">
                  Open Claims ({visibleAnimalIds.reduce((a, animal_id) => a + animalData[animal_id].open, 0)})
                </h4>
              </Card.Title>
              <p className="phr" />
              <Scrollbar no_shadow="true" style={{ height: "264px", minHeight: "264px" }} renderView={props => <div {...props} style={{ ...props.style, overflowX: "hidden", marginBottom: "-10px" }} />} renderThumbHorizontal={props => <div {...props} style={{ ...props.style, display: 'none' }} />}>
                {visibleAnimalIds.map((animal_id) => (
                  <Link href={"/" + organization + "/" + incident + "/animals/" + animalData[animal_id].id_for_incident + "/claims"} className="text-link" style={{ color: "white", textDecoration: "none" }} key={animal_id}>
                    <div className="mt-1 mb-1">
                      <div className="card-header border rounded ps-2" style={{ fontSize: "20px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        A#{animalData[animal_id].id_for_incident} - {animalData[animal_id].animal_count === 1 ? <span>{animalData[animal_id].name || 'Unknown Name'} <span style={{ textTransform: 'capitalize' }}>({animalData[animal_id].species_string})</span></span> : <span>{animalData[animal_id].animal_count} <span style={{ textTransform: "capitalize" }}>({animalData[animal_id].species_string})</span></span>}
                        {' - '}{animalData[animal_id].open} open claim{animalData[animal_id].open > 1 ? "s" : ""}
                      </div>
                    </div>
                  </Link>
                ))}
                {visibleAnimalIds.length === 0 ? <span>No animals have open claims at this time.</span> : ""}
              </Scrollbar>
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
}

export default ClaimsDashboard;
